import React, { useState } from 'react';
import { Alert, Button, Stack, Typography } from '@mui/material';
import WorkflowAbandonDialog from './WorkflowAbandonDialog';
import WorkflowRetryDialog from './WorkflowRetryDialog';
import { useWorkflowWrite } from '../hooks/useWorkflowWrite';
import { isRunActive } from '../runStatus';
import WorkflowAccessFailure from './WorkflowAccessFailure';
import type { WorkflowRun } from '../../../types/backend';

interface Props {
  run: WorkflowRun;
  labels: Map<string, string>;
  /** Each step's allowed_tools, so an access failure can say what its step needs. */
  grants: Map<string, string[]>;
  /** Omitted while workflows is switched off: a failure is shown, not retried. */
  onRetryStep?: (nodeId: string) => Promise<void>;
  /** Omitted on a surface that cannot act, such as the customer's read-only run history. The
   *  failures still show: why a run is stuck is the reason to read it, and is not a write. */
  onAbandonRun?: (reason: string) => Promise<void>;
  /** Opens the Studio on this workflow's draft. Omitted where the Studio is not available. */
  onEditWorkflow?: (workflowId: string) => void;
}

type Pending = { kind: 'retry'; nodeId: string } | { kind: 'abandon' } | null;

// What an operator can do about a run that is stuck: send a failed step back, or stop the run.
// Both are human acts the agent cannot take, and both ask first because neither can be undone.
const WorkflowRunActions: React.FC<Props> = ({
  run,
  labels,
  grants,
  onRetryStep,
  onAbandonRun,
  onEditWorkflow,
}) => {
  const [pending, setPending] = useState<Pending>(null);
  const { busy, error, clearError, write } = useWorkflowWrite();

  // A failure is open until someone retries it; the run is blocked on the open ones.
  const openFailures = (run.failures ?? []).filter((f) => !f.retried_at);
  const active = isRunActive(run.status ?? '');
  const labelOf = (nodeId: string) => labels.get(nodeId) ?? nodeId;

  const act = (go: () => Promise<void>) => {
    setPending(null);
    void write(go);
  };

  if (!active && !error && openFailures.length === 0) return null;

  return (
    <Stack spacing={1}>
      {error && (
        <Alert severity="error" onClose={clearError}>
          {error}
        </Alert>
      )}
      {openFailures.map((failure) =>
        failure.cause === 'access' && onRetryStep ? (
          <WorkflowAccessFailure
            key={`${failure.node_id}-${failure.failed_at}`}
            failure={failure}
            run={run}
            label={labelOf(failure.node_id)}
            grants={grants.get(failure.node_id) ?? []}
            busy={busy}
            onRetry={() => setPending({ kind: 'retry', nodeId: failure.node_id })}
            onEditWorkflow={onEditWorkflow}
          />
        ) : (
          <Alert
            key={`${failure.node_id}-${failure.failed_at}`}
            severity="error"
            action={onRetryStep && (
              <Button
                size="small"
                variant="contained"
                color="error"
                disabled={busy}
                onClick={() => setPending({ kind: 'retry', nodeId: failure.node_id })}
              >
                Retry step
              </Button>
            )}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Blocked: {labelOf(failure.node_id)} failed
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
              {failure.error}
            </Typography>
          </Alert>
        ),
      )}
      {active && onAbandonRun && (
        <Stack direction="row" justifyContent="flex-end">
          <Button size="small" color="error" disabled={busy} onClick={() => setPending({ kind: 'abandon' })}>
            Abandon run
          </Button>
        </Stack>
      )}

      <WorkflowRetryDialog
        open={pending?.kind === 'retry'}
        runTitle={run.workflow_ref.title}
        stepLabel={pending?.kind === 'retry' ? labelOf(pending.nodeId) : ''}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          if (pending?.kind === 'retry' && onRetryStep) {
            const { nodeId } = pending;
            act(() => onRetryStep(nodeId));
          }
        }}
      />

      <WorkflowAbandonDialog
        open={pending?.kind === 'abandon'}
        runTitle={run.workflow_ref.title}
        onCancel={() => setPending(null)}
        onConfirm={(given) => onAbandonRun && act(() => onAbandonRun(given))}
      />
    </Stack>
  );
};

export default WorkflowRunActions;
